import { batch } from "solid-js";
import { reconcile } from "solid-js/store";
import FeatureStore from "@rock-solid/feature-store";

import CommunityProfile, {
  CommunityProfileUsa,
} from "@repo/kikoff-models/community/CommunityProfile";
import usRpc from "@repo/protobuf/api/usRpc";

import { useAdmin } from "./admin";

const initialState = {
  byId: {} as Record<CommunityProfile.Id, CommunityProfile>,
  summaryById: {} as Record<CommunityProfile.Id, CommunityProfile.Summary>,
  idForUser: null as CommunityProfile.Id | null,
};

export const { useProfiles } = FeatureStore.init(
  "Profiles",
  initialState
).setup(([store, setStore], { attachAction, wrap }) => {
  const [admin] = useAdmin();

  const accessors = wrap
    .accessors({
      byId: (id: CommunityProfile.Id) => store.byId[id],
      summaryById: (
        id: CommunityProfile.Id
      ): CommunityProfile.Summary | undefined => store.summaryById[id],
      current: () => admin.profiles.current() || accessors.forUser(),
      forUser: attachAction(
        () => store.byId[store.idForUser!],
        () => actions.fetch()
      ),
    })
    .finalize();

  const mutations = wrap
    .mutations({
      one(profile: CommunityProfile) {
        batch(() => {
          setStore("byId", profile.id, reconcile(profile));
          setStore(
            "summaryById",
            profile.id,
            reconcile(CommunityProfile.toSummary(profile))
          );
        });
      },
      many(profiles: CommunityProfile[]) {
        batch(() => {
          for (const profile of profiles) {
            mutations.one(profile);
          }
        });
      },
      oneSummary(profile: CommunityProfile.Summary) {
        setStore("summaryById", profile.id, reconcile(profile));
      },
      manySummaries(profiles: CommunityProfile.Summary[]) {
        batch(() => {
          for (const profile of profiles) {
            mutations.oneSummary(profile);
          }
        });
      },
    })
    .finalize();

  const actions = wrap
    .actions({
      one: mutations.one,
      many: mutations.many,
      oneSummary: mutations.oneSummary,
      manySummaries: mutations.manySummaries,
      fetch(token?: CommunityProfile.Id) {
        return usRpc.Community.getCommunityProfile({ token })
          .SUCCESS(({ profile }) => {
            batch(() => {
              const normalized = CommunityProfileUsa.normalize(profile!);
              mutations.one(normalized);
              if (!token) setStore("idForUser", normalized.id);
            });
          })
          .elseThrow("Failed to fetch profile");
      },
    })
    .finalize();

  return { accessors, actions };
});
